/**
 * Embed Mode — public (pre-sign-in) catalog + player registration.
 *
 * These endpoints power the player iframe when it is embedded with an
 * `embed_key` instead of a server-created session: visitors browse the catalog
 * anonymously, then sign up or sign in with email + password right inside the
 * iframe at their first purchase. No security scheme applies — the only
 * credential is your `embed_key`, sent as a query parameter on reads and in
 * the JSON body on writes. An unknown, disabled, or ineligible key returns
 * `404 NOT_FOUND` with no further detail.
 *
 * You normally do not call these yourself: the iframe drives the whole flow.
 * They are documented for transparency and for partners building their own UI.
 * All embed endpoints are rate-limited per IP.
 */
import { TEMPLATE_NAMES } from '@game-platform/theme-constructor';
import { CUSTOM_PACK_UNAVAILABLE_REASONS, CUSTOM_PACK_UNPRICED_NOTE } from './components.customPack';
import {
  customPackCardsExamples,
  customPackCardsSchema,
  customPackSearchParameters,
  retryAfterHeaders,
} from './components.customPackCatalog';

// `embed_key` query parameter shared by every GET below.
const embedKeyQueryParam = {
  name: 'embed_key',
  in: 'query',
  required: true,
  schema: { type: 'string' },
  description:
    'Your embed key, issued by us when embed mode is enabled for your platform. An unknown, disabled, ' +
    'or ineligible key returns `404 NOT_FOUND`.',
};

// Shared 404 for a bad `embed_key`.
const unknownEmbedKeyResponse = {
  description: '`NOT_FOUND` — unknown, disabled, or ineligible embed key.',
  content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
};

// Success body shared by register / sign-in / reset-confirm: a freshly minted
// player session, ready to use as `X-Platform-Session` on the Player API.
const embedSessionContent = {
  'application/json': {
    schema: {
      type: 'object',
      title: 'EmbedSessionResponse',
      required: ['result'],
      properties: {
        result: {
          type: 'object',
          required: ['session_token', 'expires_at'],
          properties: {
            session_token: {
              type: 'string',
              description:
                'The player session token. Send it as `X-Platform-Session` on every Player API request — ' +
                'the same token a server-created session would carry.',
            },
            expires_at: {
              type: 'string',
              format: 'date-time',
              description:
                'When the session expires (ISO 8601). Self-registered sessions are valid for 7 days and ' +
                'slide forward while the player stays active.',
            },
          },
        },
      },
    },
    example: {
      result: {
        session_token: 'Yl9kF3oQb2c1d4eR5tY6uI7oP8aS9dF0gH1jK2lZ',
        expires_at: '2026-07-25T17:31:00Z',
      },
    },
  },
};

export const embedPaths: Record<string, unknown> = {
  '/loot_machine/api/game_platform/iframe/v1/public/design_system': {
    get: {
      tags: ['Embed Mode'],
      summary: 'Get the resolved theme (anonymous)',
      description: [
        'Anonymous twin of *Get the resolved theme* (Player Misc): returns the template and theme tokens',
        'for your platform before any player is signed in, so the iframe renders the storefront with your',
        'branding straight from the embed URL.',
        '',
        'On top of the theme it carries the embed boot flags: the funding mode (always `stripe` in embed',
        'mode) and the `registration` block, which tells the iframe that sign-up is required and that the',
        'current visitor is not signed in yet.',
      ].join('\n'),
      operationId: 'embedGetTheme',
      security: [],
      parameters: [embedKeyQueryParam],
      responses: {
        '200': {
          description: 'The resolved template, theme tokens, and embed boot flags.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'EmbedTheme',
                required: [
                  'template',
                  'funding_mode',
                  'allow_withdrawals',
                  'design_system',
                  'disable_shipping',
                  'registration',
                ],
                properties: {
                  template: {
                    type: 'string',
                    enum: [...TEMPLATE_NAMES],
                    description:
                      'The layout to render. Embed mode has no session, so this is always your account ' +
                      'default template.',
                  },
                  funding_mode: {
                    type: 'string',
                    enum: ['stripe'],
                    description: "Always 'stripe' — embed mode requires Stripe funding.",
                  },
                  allow_withdrawals: {
                    type: 'boolean',
                    description: 'Always false before sign-in.',
                  },
                  design_system: {
                    type: 'object',
                    description:
                      'Theme tokens for the template — identical shape to `design_system` on ' +
                      '*Get the resolved theme* (Player Misc): the version, a color palette, and optional ' +
                      '`overrides`, `typography`, `radius` and `shadow` blocks.',
                    required: ['version', 'palette'],
                    properties: {
                      version: { type: 'integer', enum: [2], description: 'Theme schema version. Always 2.' },
                      palette: {
                        type: 'object',
                        additionalProperties: true,
                        description:
                          'Color tokens for the theme (all hex strings) — same keys as the session-authed endpoint.',
                      },
                    },
                  },
                  disable_shipping: {
                    type: 'boolean',
                    description: 'Always false in embed mode.',
                  },
                  registration: {
                    type: 'object',
                    title: 'EmbedRegistrationState',
                    required: ['mode', 'registered'],
                    properties: {
                      mode: {
                        type: 'string',
                        enum: ['required'],
                        description: "Always 'required' in embed mode.",
                      },
                      registered: {
                        type: 'boolean',
                        description: 'Always false — no player is signed in on this endpoint.',
                      },
                    },
                  },
                },
              },
              example: {
                template: 'modern',
                funding_mode: 'stripe',
                allow_withdrawals: false,
                design_system: {
                  version: 2,
                  palette: {
                    primary: '#FF4D4D',
                    textOnBrand: '#FFFFFF',
                    accent: '#FF4D4D',
                    surfaceBorder: '#262626',
                    heroFrom: '#1A0B0B',
                    heroTo: '#000000',
                    textPrimary: '#FFFFFF',
                  },
                },
                disable_shipping: false,
                registration: { mode: 'required', registered: false },
              },
            },
          },
        },
        '404': unknownEmbedKeyResponse,
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/pool_categories': {
    get: {
      tags: ['Embed Mode'],
      summary: 'List game categories (anonymous)',
      description: [
        'Anonymous twin of *List game categories* (Catalog): the category pills for your platform, with',
        'no player session. The response shape is identical to the session-authed endpoint — see',
        '*List game categories* for the field-level reference.',
      ].join('\n'),
      operationId: 'embedListGameCategories',
      security: [],
      parameters: [embedKeyQueryParam],
      responses: {
        '200': {
          description: 'Available game categories — same shape as *List game categories*.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'EmbedCategoryList',
                required: ['categories'],
                properties: {
                  categories: {
                    type: 'array',
                    description:
                      'One entry per category with at least one available game — the same `GameCategory` ' +
                      'shape as the session-authed endpoint.',
                    items: { type: 'object', additionalProperties: true },
                  },
                },
              },
              example: {
                categories: [
                  {
                    id: 430,
                    category_id: 958,
                    category_name: 'MMA',
                    category_type: 'pack_category',
                    image: 'https://cdn.getcardbase.com/blobs/cat_mma.png',
                  },
                ],
              },
            },
          },
        },
        '404': unknownEmbedKeyResponse,
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/pool_offers': {
    get: {
      tags: ['Embed Mode'],
      summary: 'List available games (anonymous)',
      description: [
        'Anonymous twin of *List available games* (Catalog): the paginated storefront catalog for your',
        'platform, with no player session. It accepts the same filtering, sorting and pagination query',
        'parameters as the session-authed endpoint (category scoping, `order_by` / `order_direction`,',
        '`page` / `per`), and the response shape is identical — see *List available games* for the',
        'field-level reference.',
        '',
        'Custom pack games (`game_kind: custom_pack`) are not listed here either. A custom pack is opened by its id,',
        'which the server-side `GET /v1/games` returns with `game_type: "custom_pack"`.',
      ].join('\n'),
      operationId: 'embedListGames',
      security: [],
      parameters: [
        embedKeyQueryParam,
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 1, minimum: 1 },
          description: '1-based page number. Defaults to 1.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 32, maximum: 100 },
          description: 'Number of games per page. Defaults to 32, capped at 100.',
        },
      ],
      responses: {
        '200': {
          description: 'A page of games — same shape as *List available games*.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'EmbedGameList',
                required: ['pool_offers', 'meta'],
                properties: {
                  pool_offers: {
                    type: 'array',
                    description:
                      'The games on this page — the same `GameCard` shape as the session-authed endpoint.',
                    items: { type: 'object', additionalProperties: true },
                  },
                  meta: { $ref: '#/components/schemas/PaginationMeta' },
                },
              },
              example: {
                pool_offers: [
                  {
                    id: 463,
                    title: 'HOF ELITE MMA Pack',
                    price: '99.99',
                    status: 'available',
                    pack_image_url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                    min_value: 5,
                    max_value: 500,
                    big_win_chance: 21.5,
                  },
                ],
                meta: { total_count: 12, page: 1, per: 32 },
              },
            },
          },
        },
        '404': unknownEmbedKeyResponse,
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/pool_offers/{id}': {
    get: {
      tags: ['Embed Mode'],
      summary: 'Get a game (anonymous)',
      description: [
        'Anonymous twin of *Get a game* (Catalog): a single game with its full odds breakdown, pricing,',
        'and media assets, with no player session. The response shape is identical to the session-authed',
        'endpoint — see *Get a game* for the field-level reference. As there, a game whose `game_kind` is',
        '`custom_pack` has no odds fields and carries `custom_pack` (the RTP and the limits to build within) instead.',
      ].join('\n'),
      operationId: 'embedGetGame',
      security: [],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Identifier of the game to retrieve.',
        },
        embedKeyQueryParam,
      ],
      responses: {
        '200': {
          description: 'The requested game — same shape as *Get a game*.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'EmbedGameDetailResponse',
                required: ['pool_offer'],
                properties: {
                  pool_offer: {
                    type: 'object',
                    description:
                      'The game — the same `GameDetail` shape as the session-authed endpoint, including, for a ' +
                      '`loot_pack`, the per-tier `odds` and the per-volatility `odds_levels`.',
                    additionalProperties: true,
                  },
                },
              },
              example: {
                pool_offer: {
                  id: 463,
                  title: 'HOF ELITE MMA Pack',
                  game_kind: 'loot_pack',
                  price: '99.99',
                  status: 'available',
                  pack_image_url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                  estimated_value: 120,
                  min_value: 5,
                  odds: [
                    { tier_name: 'Common', range: '$5 - $50', percentage: 78.5 },
                    { tier_name: 'Uncommon', range: '$50 - $200', percentage: 18 },
                    { tier_name: 'Legendary', range: '$500+', percentage: 3.5 },
                  ],
                  big_win_chance: 21.5,
                },
              },
            },
          },
        },
        '404': {
          description:
            '`NOT_FOUND` — unknown, disabled, or ineligible embed key, or no game with this id is ' +
            `available on your platform. ${CUSTOM_PACK_UNPRICED_NOTE}`,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/pool_offers/{pool_offer_id}/pool_items': {
    get: {
      tags: ['Embed Mode'],
      summary: 'Cards you can pull from a game (anonymous)',
      description: [
        'Anonymous twin of *Cards you can pull from a game* (Catalog): the paginated "potential hits"',
        'grid for a game, sorted from highest value down, with no player session. The response shape is',
        'identical to the session-authed endpoint — see *Cards you can pull from a game* for the',
        'field-level reference.',
      ].join('\n'),
      operationId: 'embedListGameCards',
      security: [],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Identifier of the game whose cards you want to list.',
        },
        embedKeyQueryParam,
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 1, minimum: 1 },
          description: '1-based page number. Defaults to 1.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 32, maximum: 100 },
          description: 'Number of cards per page. Defaults to 32, capped at 100.',
        },
      ],
      responses: {
        '200': {
          description: 'A page of cards available in the game — same shape as *Cards you can pull from a game*.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'EmbedGameCardGrid',
                required: ['pool_items', 'meta'],
                properties: {
                  pool_items: {
                    type: 'array',
                    description:
                      'The cards on this page, ordered from highest value down — the same `Card` shape as ' +
                      'the session-authed endpoint.',
                    items: { type: 'object', additionalProperties: true },
                  },
                  meta: { $ref: '#/components/schemas/PaginationMeta' },
                },
              },
              example: {
                pool_items: [
                  {
                    id: 778231,
                    title: 'Conor McGregor 2021 Prizm Auto /25',
                    image_url: 'https://cdn.getcardbase.com/blobs/item_778231.png',
                    item_price: 1450.0,
                    badge: { id: 7, name: 'Legendary' },
                  },
                ],
                meta: { total_count: 50, page: 1, per: 32 },
              },
            },
          },
        },
        '404': {
          description:
            '`NOT_FOUND` — unknown, disabled, or ineligible embed key, or no game with this id is ' +
            'available on your platform.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/pool_offers/{pool_offer_id}/custom_pack/items': {
    get: {
      tags: ['Embed Mode'],
      summary: 'Search cards for a custom pack (anonymous)',
      description: [
        'Anonymous twin of *Search cards for a custom pack* (Catalog): a visitor who has not signed in browses the',
        'cards of a custom pack game and builds a selection. It takes the same filters, sort and paging parameters',
        'and returns the same response — see *Search cards for a custom pack* for the field-level reference,',
        'including how paging stops at the search window.',
        '',
        'Pricing a selection (*Get a custom pack quote*) and buying need a signed-in player session: when the',
        'visitor presses Rip, the iframe opens the sign-in and then buys the same selection.',
        '',
        'The search has its own rate limit: 60 requests per 60 seconds per IP address, and 600 requests per 60',
        'seconds per embed key.',
      ].join('\n'),
      operationId: 'embedSearchCustomPackCards',
      security: [],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'path',
          required: true,
          schema: { type: 'integer' },
          description: 'Identifier of the custom pack game — one whose `game_kind` is `custom_pack`.',
        },
        embedKeyQueryParam,
        ...customPackSearchParameters,
      ],
      responses: {
        '200': {
          description:
            'A page of cards — same shape as *Search cards for a custom pack*: page 1 also carries the filter options ' +
            '(`facets`, unless the request sent `facets=0`) and the price ranges (`price`) to offer, and a later page ' +
            'carries `items` and `meta` only.',
          content: { 'application/json': { schema: customPackCardsSchema, examples: customPackCardsExamples } },
        },
        '404': {
          description:
            '`NOT_FOUND` — unknown, disabled, or ineligible embed key, or no available custom pack has this id: ' +
            'the game does not exist or is a regular pack, or the custom pack is unavailable because ' +
            `${CUSTOM_PACK_UNAVAILABLE_REASONS}.`,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '429': {
          description:
            '`RATE_LIMITED` — more than 60 requests in 60 seconds per IP address, or more than 600 requests in ' +
            '60 seconds per embed key. `CATALOG_BUSY` — the search did not answer in four seconds because it is ' +
            'busy. Wait `Retry-After` seconds before searching again, and not in a tight loop; the request ' +
            'changed nothing.',
          headers: retryAfterHeaders,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '503': {
          description:
            '`SEARCH_UNAVAILABLE` — the card search is temporarily down. Retry shortly, and not in a tight loop; ' +
            'the request changed nothing.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/pool_items/just_pulled': {
    get: {
      tags: ['Embed Mode'],
      summary: 'List recent reveals (anonymous)',
      description: [
        'Anonymous twin of *List recent reveals (just-pulled feed)* (Inventory & Feeds): the live',
        '"just pulled" ticker of recently revealed cards across all players, newest first, with no',
        'player session. It accepts the same `pool_offer_id` / `min_fair_market_value` filters and',
        'pagination as the session-authed endpoint, and the response shape is identical — see',
        '*List recent reveals (just-pulled feed)* for the field-level reference.',
      ].join('\n'),
      operationId: 'embedListJustPulledFeed',
      security: [],
      parameters: [
        embedKeyQueryParam,
        {
          name: 'pool_offer_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description:
            'Optional game (pack) id. When set, the feed is limited to reveals from that pack. When omitted, the feed spans all games.',
        },
        {
          name: 'min_fair_market_value',
          in: 'query',
          required: false,
          schema: { type: 'number' },
          description:
            'Optional minimum fair market value, in USD (e.g. `100` for $100). When set, only pulls with a fair market value at or above this amount are returned; cards without a known fair market value are excluded.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 1, minimum: 1 },
          description: '1-based page number. Defaults to 1.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 32, maximum: 100 },
          description: 'Number of cards per page. Defaults to 32, capped at 100.',
        },
      ],
      responses: {
        '200': {
          description:
            'A page of recently revealed cards — same shape as *List recent reveals (just-pulled feed)*.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'EmbedJustPulledFeed',
                required: ['pool_items', 'meta'],
                properties: {
                  pool_items: {
                    type: 'array',
                    description:
                      'The reveals on this page, newest first — the same `JustPulledCard` shape as the ' +
                      'session-authed endpoint.',
                    items: { type: 'object', additionalProperties: true },
                  },
                  meta: { $ref: '#/components/schemas/PaginationMeta' },
                },
              },
              example: {
                pool_items: [
                  {
                    id: 778231,
                    title: 'Conor McGregor 2021 Prizm Auto /25',
                    image_url: 'https://cdn.getcardbase.com/blobs/item_778231.png',
                    opened_at: '2026-07-17T16:58:12Z',
                    fair_market_value: 1450.0,
                    badge: { id: 7, name: 'Legendary' },
                    pool_offer: {
                      id: 463,
                      title: 'HOF ELITE MMA Pack',
                      image_url: 'https://cdn.getcardbase.com/blobs/pack_463.png',
                    },
                  },
                ],
                meta: { total_count: 240, page: 1, per: 32 },
              },
            },
          },
        },
        '404': unknownEmbedKeyResponse,
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/registrations': {
    post: {
      tags: ['Embed Mode'],
      summary: 'Create a player account',
      description: [
        'Registers a new player with email + password and signs them in, returning a ready-to-use player',
        'session token — the embed-mode counterpart of *Create a launch session*. The iframe calls this',
        'from its sign-up form at the first purchase.',
        '',
        'The password must be at least 8 characters. The email is not verified at sign-up (ownership is',
        'proven later by the password-reset code), and accounts are scoped to your platform: the same',
        'email on another platform is an independent account. If the email is already registered on your',
        'platform this returns `email_taken` — the player should sign in instead.',
      ].join('\n'),
      operationId: 'embedRegister',
      security: [],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'EmbedRegisterRequest',
              required: ['embed_key', 'email', 'password'],
              properties: {
                embed_key: { type: 'string', description: 'Your embed key.' },
                email: { type: 'string', format: 'email', description: "The player's email address." },
                password: {
                  type: 'string',
                  minLength: 8,
                  description: "The player's chosen password. At least 8 characters.",
                },
              },
            },
            example: {
              embed_key: 'k3J9mQvXw1JZC5rT8uHhYw',
              email: 'player@example.com',
              password: 'correct-horse-battery',
            },
          },
        },
      },
      responses: {
        '201': { description: 'Account created and signed in.', content: embedSessionContent },
        '404': unknownEmbedKeyResponse,
        '422': {
          description:
            'Registration rejected — `email_taken` (already registered on your platform: sign in ' +
            'instead), `invalid_email` (malformed or undeliverable address), or `weak_password` (fewer ' +
            'than 8 characters).',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/registrations/sign_in': {
    post: {
      tags: ['Embed Mode'],
      summary: 'Sign a player in',
      description: [
        'Signs an existing player in with email + password and returns a fresh player session token.',
        'Any previous active session for the player is ended — one active session per player.',
        '',
        'Accounts auto-registered through the session API (`user` in *Create a launch session*) have no',
        'password yet: signing in with such an email returns `password_not_set`, and the player sets one',
        'through the password-reset flow below.',
      ].join('\n'),
      operationId: 'embedSignIn',
      security: [],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'EmbedSignInRequest',
              required: ['embed_key', 'email', 'password'],
              properties: {
                embed_key: { type: 'string', description: 'Your embed key.' },
                email: { type: 'string', format: 'email', description: "The player's email address." },
                password: { type: 'string', description: "The player's password." },
              },
            },
            example: {
              embed_key: 'k3J9mQvXw1JZC5rT8uHhYw',
              email: 'player@example.com',
              password: 'correct-horse-battery',
            },
          },
        },
      },
      responses: {
        '200': { description: 'Signed in.', content: embedSessionContent },
        '401': {
          description: '`invalid_credentials` — wrong email or password (we do not reveal which).',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': unknownEmbedKeyResponse,
        '422': {
          description:
            '`password_not_set` — the account exists but has no password yet (it was auto-registered ' +
            'through the session API). Use the password-reset flow to set one.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/registrations/reset/send_code': {
    post: {
      tags: ['Embed Mode'],
      summary: 'Send a password-reset code',
      description: [
        'Emails the player a 6-digit code to reset — or initially set — their password. Always returns',
        '`200` with `sent: true` whether or not the email has an account, so account existence is never',
        'revealed. The code expires after 10 minutes; a new code can be requested at most once per',
        'minute.',
      ].join('\n'),
      operationId: 'embedResetSendCode',
      security: [],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'EmbedResetSendCodeRequest',
              required: ['embed_key', 'email'],
              properties: {
                embed_key: { type: 'string', description: 'Your embed key.' },
                email: { type: 'string', format: 'email', description: "The player's email address." },
              },
            },
            example: { embed_key: 'k3J9mQvXw1JZC5rT8uHhYw', email: 'player@example.com' },
          },
        },
      },
      responses: {
        '200': {
          description: 'Accepted. `sent` is always true — account existence is not revealed.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'EmbedResetSendCodeResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['sent'],
                    properties: {
                      sent: { type: 'boolean', enum: [true], description: 'Always true.' },
                    },
                  },
                },
              },
              example: { result: { sent: true } },
            },
          },
        },
        '404': unknownEmbedKeyResponse,
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/public/registrations/reset/confirm': {
    post: {
      tags: ['Embed Mode'],
      summary: 'Confirm a password-reset code',
      description: [
        'Verifies the emailed code, sets the new password, and signs the player in (auto-login),',
        'returning a fresh session token. This is also how players auto-registered through the session',
        'API set their first password.',
        '',
        'A code allows at most 5 attempts and is burned on success. The new password must be at least',
        '8 characters.',
      ].join('\n'),
      operationId: 'embedResetConfirm',
      security: [],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'EmbedResetConfirmRequest',
              required: ['embed_key', 'email', 'otp', 'new_password'],
              properties: {
                embed_key: { type: 'string', description: 'Your embed key.' },
                email: { type: 'string', format: 'email', description: "The player's email address." },
                otp: { type: 'string', description: 'The 6-digit code from the email.' },
                new_password: {
                  type: 'string',
                  minLength: 8,
                  description: 'The new password. At least 8 characters.',
                },
              },
            },
            example: {
              embed_key: 'k3J9mQvXw1JZC5rT8uHhYw',
              email: 'player@example.com',
              otp: '482913',
              new_password: 'correct-horse-battery',
            },
          },
        },
      },
      responses: {
        '200': { description: 'Password set; the player is signed in.', content: embedSessionContent },
        '404': unknownEmbedKeyResponse,
        '422': {
          description:
            'Rejected — `invalid_code` (wrong or expired code), `too_many_attempts` (more than 5 tries ' +
            'on this code: request a new one), or `weak_password` (fewer than 8 characters).',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },
};
